import type { VideoStatus } from "@coinfallsmp/shared";
import { creatorBadge } from "@coinfallsmp/shared";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useUploadsEnabled } from "../AppConfigContext.js";
import { canUploadNow, creatorOf, useAuth } from "../AuthContext.js";
import { listVideos, type AdminVideo } from "../api.js";
import CreatorBadge from "../components/CreatorBadge.js";
import StatusBadge from "../components/StatusBadge.js";
import Landing from "./Landing.js";

const FILTERS: Array<VideoStatus | "all"> = [
  "all",
  "draft",
  "processing",
  "published",
  "failed",
];

export default function Library() {
  const { identity, loading: authLoading } = useAuth();
  const creator = creatorOf(identity);
  const isAdmin = creator?.isAdmin ?? false;
  const uploadAllowed = canUploadNow(identity);
  const uploadsEnabled = useUploadsEnabled();
  const [filter, setFilter] = useState<VideoStatus | "all">("all");
  const [videos, setVideos] = useState<AdminVideo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!creator) return;
    setLoading(true);
    listVideos(filter)
      .then((res) => setVideos(res.items))
      .finally(() => setLoading(false));
  }, [filter, creator]);

  if (authLoading) return <p className="status-message">Loading…</p>;
  if (!creator) return <Landing />;

  return (
    <section>
      <div className="page-header">
        <h1>{isAdmin ? "Library" : "My Videos"}</h1>
        {uploadAllowed ? (
          <div className="header-actions">
            <Link to="/import" className={uploadsEnabled ? "button secondary" : "button primary"}>
              Import from YouTube
            </Link>
            {uploadsEnabled ? (
              <Link to="/new" className="button primary">
                Upload video
              </Link>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="filter-tabs">
        {FILTERS.map((f) => (
          <button
            key={f}
            className={f === filter ? "tab active" : "tab"}
            onClick={() => setFilter(f)}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="status-message">Loading…</p>
      ) : videos.length === 0 ? (
        <p className="status-message">No videos here yet.</p>
      ) : (
        <table className="video-table">
          <thead>
            <tr>
              <th>Title</th>
              {isAdmin ? <th>Uploader</th> : null}
              <th>Status</th>
              <th>Source</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {videos.map((video) => (
              <tr key={video.id}>
                <td>
                  <Link to={`/videos/${video.id}`}>{video.title}</Link>
                </td>
                {isAdmin ? (
                  <td>
                    {video.uploader ? (
                      <Link to={`/creators/${video.uploader.id}`}>
                        <CreatorBadge badge={video.uploader.badge} /> {video.uploader.displayName}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                ) : null}
                <td>
                  <StatusBadge status={video.status} />
                </td>
                <td>{video.sourceType}</td>
                <td>{new Date(video.createdAt).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
